import { getIn } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FitoListReactQuery } from '~/Components';
import {
  IFitoList,
  IFitoListSettings,
} from '~/Components/FitoList/FitoListUncontrolled/interfaces';
import { Group } from '~/Components/Icons/Group';
import { UserRole } from '~/Models/DTO/Role.model';
import { IStudio } from '~/Models/DTO/Studio.model';
import { IOrgResource, IOrgRoles } from '~/Models/interfaces/OrgStaff.model';
import { useItemCreationContext } from '~/ModularComponents';
import { InputSelector, ItemCreationInputTemplate } from '~/ModularComponents/ItemCreationInputs';
import { useRequestOrgStudioList } from '~/requests';
import { Avatar } from '~/UIKit';

const createListItem = (item: IStudio): IFitoList => ({
  header: item.name,
  picture: <Avatar imageUrl={item.coverurl} />,
  checkable: true,
});

export const StaffRoleSelect = () => {
  const { t } = useTranslation();
  const { values: staff, setFieldValue } = useItemCreationContext();

  const trainerRoles = getIn(staff, 'trainerRoles') as IOrgResource[];

  const fullAccessRoles = getIn(staff, 'fullAccessRoles') as IOrgResource[];
  const restrictedRoles = getIn(staff, 'restrictedRoles') as IOrgResource[];
  const organisationRoles = getIn(staff, 'organisationRoles') as IOrgResource[];

  const trainerRoleIds = trainerRoles?.map((role) => role.id);
  const fullAccessRoleIds = fullAccessRoles?.map((role) => role.id);
  const restrictedRoleIds = restrictedRoles?.map((role) => role.id);
  const organisationRoleIds = organisationRoles?.map((role) => role.id);

  const onSelect = (fieldName: string, list: IOrgResource[]) => (
    ids: string | string[],
    checked = false,
  ) => {
    const listOfSelectedIds = typeof ids === 'string' ? [ids] : ids;
    const listOfSelectedResources: IOrgResource[] = listOfSelectedIds?.map((id) => ({
      id,
      name: '',
      resourceType: 'PROVIDER',
    }));

    if (checked) {
      setFieldValue(fieldName, [
        ...list,
        ...listOfSelectedResources.filter(
          (resource) => list.filter((item) => item.id === resource.id).length === 0,
        ),
      ]);
    } else {
      setFieldValue(
        fieldName,
        list.filter(
          (item) =>
            listOfSelectedResources.filter((resource) => resource.id === item.id).length === 0,
        ),
      );
    }
  };

  useEffect(() => {
    const roles: IOrgRoles[] = [];
    if (fullAccessRoles?.length) {
      roles.push({
        accessType: UserRole.FULL_ACCESS,
        resources: fullAccessRoles,
      });
    }
    if (restrictedRoles?.length) {
      roles.push({
        accessType: UserRole.RESTRICTED,
        resources: restrictedRoles,
      });
    }
    if (trainerRoles?.length) {
      roles.push({
        accessType: UserRole.TRAINER,
        resources: trainerRoles,
      });
    }
    if (organisationRoles?.length) {
      roles.push({
        accessType: UserRole.ORGANISATION_MANAGER,
        resources: organisationRoles,
      });
    }
    setFieldValue('roles', roles);
  }, [trainerRoles, fullAccessRoles, restrictedRoles, organisationRoles]);

  const settings: IFitoListSettings = {
    search: false,
    pagination: true,
    multiSelect: true,
    select: true,
    headerPagination: {
      skip: 0,
      take: 10,
    },
  };
  const setItemValues = (item: IStudio): IFitoList => createListItem(item);

  const inputElement = ({
    excludedIds,
    inputName,
    list,
  }: {
    excludedIds: string[];
    list: IOrgResource[];
    inputName: string;
  }) => (
    <InputSelector buttonText={t('form.selectLocations')} name={inputName}>
      <FitoListReactQuery<IStudio>
        keyOfId="uuid"
        settings={settings}
        title={t('form.selectLocations')}
        setItemValues={setItemValues}
        pageSize={10}
        defaultQuery={excludedIds}
        queryResults={useRequestOrgStudioList}
        size="small"
        onSelect={onSelect(inputName, list)}
        defaultSelectedIds={list.map((item) => item.id)}
      />
    </InputSelector>
  );

  return (
    <div>
      <ItemCreationInputTemplate
        name={'roles'}
        icon={<Group />}
        label={t('settings.staff.accessType.2')}
        InputElement={inputElement({
          inputName: 'trainerRoles',
          list: trainerRoles,
          excludedIds: [...fullAccessRoleIds, ...restrictedRoleIds, ...organisationRoleIds],
        })}
      />
      <ItemCreationInputTemplate
        name={'roles'}
        icon={<Group />}
        label={t('settings.staff.accessType.1')}
        InputElement={inputElement({
          inputName: 'fullAccessRoles',
          list: fullAccessRoles,
          excludedIds: [...trainerRoleIds, ...restrictedRoleIds, ...organisationRoleIds],
        })}
      />
      <ItemCreationInputTemplate
        name={'roles'}
        icon={<Group />}
        label={t('settings.staff.accessType.3')}
        InputElement={inputElement({
          inputName: 'restrictedRoles',
          list: restrictedRoles,
          excludedIds: [...trainerRoleIds, ...fullAccessRoleIds, ...organisationRoleIds],
        })}
      />
    </div>
  );
};
